import React, { useState, useEffect } from 'react';
import { emailTemplatesApi, EmailTemplateResponse } from '../api/client';
import { getErrorMessage } from '../utils/errorHandler';
import RichTextEditor from './email/RichTextEditor';

interface EmailTemplateManagerProps {
  onClose: () => void;
  onTemplateSelect?: (templateId: number) => void;
  category?: string; // Filter templates by category
}

const EmailTemplateManager: React.FC<EmailTemplateManagerProps> = ({ onClose, onTemplateSelect, category }) => {
  const [templates, setTemplates] = useState<EmailTemplateResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplateResponse | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    body: '',
    category: category || 'invitation',
    is_active: true
  });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<number | null>(null);
  const [editorMode, setEditorMode] = useState<'rich' | 'source' | 'preview'>('rich');
  const [previewTemplate, setPreviewTemplate] = useState<EmailTemplateResponse | null>(null);

  useEffect(() => {
    loadTemplates();
  }, [category]);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const response = await emailTemplatesApi.listTemplates(category);
      setTemplates(response);
    } catch (error) {
      alert(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleNewTemplate = () => {
    setEditingTemplate(null);
    setFormData({
      name: '',
      subject: 'You\'re invited to {{event_name}}!',
      body: `<p>Dear {{name}},</p>
<p>You are cordially invited to <strong>{{event_name}}</strong>.</p>
<p><strong>Date:</strong> {{event_date}}<br>
<strong>Venue:</strong> {{event_venue}}</p>
<p>We look forward to seeing you there!</p>
<p>Best regards,<br>
{{organizer_name}}</p>`,
      category: category || 'invitation',
      is_active: true
    });
    setShowEditor(true);
    setEditorMode('rich');
  };

  const handleEdit = (template: EmailTemplateResponse) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      subject: template.subject,
      body: template.body,
      category: template.category || 'invitation',
      is_active: template.is_active
    });
    setShowEditor(true);
    setEditorMode('rich');
  };

  const handleSave = async () => {
    if (!formData.name || !formData.subject || !formData.body) {
      alert('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      const templateData = {
        name: formData.name,
        subject: formData.subject,
        body: formData.body,
        category: formData.category,
        is_active: formData.is_active
      };

      if (editingTemplate) {
        await emailTemplatesApi.updateTemplate(editingTemplate.id, templateData);
      } else {
        await emailTemplatesApi.createTemplate(templateData);
      }

      await loadTemplates();
      setShowEditor(false);
      alert('Template saved successfully!');
    } catch (error) {
      alert(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (templateId: number) => {
    if (!window.confirm('Are you sure you want to delete this template?')) {
      return;
    }

    setDeleting(templateId);
    try {
      await emailTemplatesApi.deleteTemplate(templateId);
      await loadTemplates();
      alert('Template deleted successfully!');
    } catch (error) {
      alert(getErrorMessage(error));
    } finally {
      setDeleting(null);
    }
  };

  const handleDuplicate = async (templateId: number) => {
    try {
      const newName = window.prompt('Enter a name for the duplicated template:');
      if (!newName) return;

      // Get the template to duplicate
      const template = templates.find(t => t.id === templateId);
      if (!template) return;

      // Create a new template with the same content but new name
      await emailTemplatesApi.createTemplate({
        name: newName,
        subject: template.subject,
        body: template.body,
        category: template.category,
        is_active: true
      });

      await loadTemplates();
      alert('Template duplicated successfully!');
    } catch (error) {
      alert(getErrorMessage(error));
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-hidden">
        <div className="p-6 border-b">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">
              Manage {category ? category.charAt(0).toUpperCase() + category.slice(1) : 'Email'} Templates
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              ✕
            </button>
          </div>
        </div>

        {showEditor ? (
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Template Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder="e.g., Conference Invitation"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="invitation">Invitation</option>
                  <option value="reminder">Reminder</option>
                  <option value="update">Update</option>
                  <option value="thank_you">Thank You</option>
                  <option value="general">General</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subject Line <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder="You're invited to {{event_name}}!"
                />
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Message Body <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setEditorMode('rich')}
                      className={`px-3 py-1 text-sm rounded ${
                        editorMode === 'rich'
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      Rich Text
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditorMode('source')}
                      className={`px-3 py-1 text-sm rounded ${
                        editorMode === 'source'
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      Source HTML
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditorMode('preview')}
                      className={`px-3 py-1 text-sm rounded ${
                        editorMode === 'preview'
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      Preview
                    </button>
                  </div>
                </div>

                {editorMode === 'rich' ? (
                  <RichTextEditor
                    content={formData.body}
                    onChange={(html) => setFormData({ ...formData, body: html })}
                    placeholder="Write your template content here..."
                  />
                ) : editorMode === 'source' ? (
                  <textarea
                    value={formData.body}
                    onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                    rows={12}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 font-mono text-sm"
                    placeholder="<p>Dear {{name}},</p>..."
                  />
                ) : (
                  <div className="border border-gray-300 rounded-md p-4 bg-gray-50 min-h-[300px]">
                    <div className="mb-3 pb-3 border-b border-gray-200">
                      <h3 className="font-semibold text-gray-700">Subject Preview:</h3>
                      <p className="mt-1 text-gray-600">
                        {formData.subject.replace(/\{\{(\w+)\}\}/g, (match, variable) => {
                          const previewValues: { [key: string]: string } = {
                            name: 'John Doe',
                            company: 'Example Corp',
                            event_name: 'Annual Conference 2024',
                            event_date: 'March 15, 2024 at 9:00 AM',
                            event_venue: 'Convention Center',
                            google_maps_link: 'https://maps.google.com',
                            organizer_name: 'Jane Smith',
                            organizer_company: 'Event Organizers Inc.'
                          };
                          return previewValues[variable] || match;
                        })}
                      </p>
                    </div>
                    <div className="prose prose-sm max-w-none">
                      <div
                        dangerouslySetInnerHTML={{
                          __html: formData.body.replace(/\{\{(\w+)\}\}/g, (match, variable) => {
                            const previewValues: { [key: string]: string } = {
                              name: 'John Doe',
                              company: 'Example Corp',
                              event_name: 'Annual Conference 2024',
                              event_date: 'March 15, 2024 at 9:00 AM',
                              event_venue: 'Convention Center',
                              google_maps_link: 'https://maps.google.com',
                              organizer_name: 'Jane Smith',
                              organizer_company: 'Event Organizers Inc.'
                            };
                            return `<span class="bg-yellow-100 px-1 rounded">${previewValues[variable] || match}</span>`;
                          })
                        }}
                      />
                    </div>
                  </div>
                )}

                <p className="mt-2 text-xs text-gray-500">
                  Available variables: {'{{name}}'}, {'{{company}}'}, {'{{event_name}}'}, {'{{event_date}}'}, {'{{event_venue}}'}, {'{{google_maps_link}}'}, {'{{organizer_name}}'}, {'{{organizer_company}}'}
                </p>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="mr-2"
                />
                <label htmlFor="is_active" className="text-sm text-gray-700">
                  Template is active
                </label>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => setShowEditor(false)}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? 'Saving...' : (editingTemplate ? 'Update Template' : 'Create Template')}
              </button>
            </div>
          </div>
        ) : (
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
            <div className="flex justify-between items-center mb-4">
              <p className="text-sm text-gray-600">
                {templates.length} template{templates.length !== 1 ? 's' : ''} available
              </p>
              <button
                onClick={handleNewTemplate}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                New Template
              </button>
            </div>

            {loading ? (
              <div className="text-center py-8 text-gray-500">Loading templates...</div>
            ) : templates.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>No templates yet.</p>
                <button
                  onClick={handleNewTemplate}
                  className="mt-2 text-blue-600 hover:text-blue-800"
                >
                  Create your first template
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {templates.map(template => (
                  <div key={template.id} className="border rounded-lg p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-gray-900">{template.name}</h3>
                          {template.category && (
                            <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                              {template.category}
                            </span>
                          )}
                          {!template.is_active && (
                            <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded">
                              Inactive
                            </span>
                          )}
                          {template.is_default && (
                            <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">
                              Default
                            </span>
                          )}
                        </div>
                        <p className="mt-2 text-sm text-gray-700">
                          <strong>Subject:</strong> {template.subject}
                        </p>
                        <div className="mt-1 text-sm text-gray-600">
                          <div
                            className="line-clamp-2"
                            dangerouslySetInnerHTML={{
                              __html: template.body.replace(/<[^>]*>/g, ' ').substring(0, 150) + '...'
                            }}
                          />
                        </div>
                      </div>
                      <div className="flex gap-2 ml-4">
                        {onTemplateSelect && (
                          <button
                            onClick={() => {
                              onTemplateSelect(template.id);
                              onClose();
                            }}
                            className="text-sm text-blue-600 hover:text-blue-800"
                          >
                            Select
                          </button>
                        )}
                        <button
                          onClick={() => setPreviewTemplate(template)}
                          className="text-sm text-blue-600 hover:text-blue-800"
                        >
                          Preview
                        </button>
                        <button
                          onClick={() => handleEdit(template)}
                          className="text-sm text-gray-600 hover:text-gray-800"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDuplicate(template.id)}
                          className="text-sm text-gray-600 hover:text-gray-800"
                        >
                          Duplicate
                        </button>
                        {!template.is_default && (
                          <button
                            onClick={() => handleDelete(template.id)}
                            disabled={deleting === template.id}
                            className="text-sm text-red-600 hover:text-red-800 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {deleting === template.id ? 'Deleting...' : 'Delete'}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="flex justify-end mt-6">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Preview Modal */}
      {previewTemplate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">Template Preview: {previewTemplate.name}</h3>
                <button
                  onClick={() => setPreviewTemplate(null)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(80vh-200px)]">
              <div className="mb-4 pb-4 border-b">
                <h4 className="font-semibold text-gray-700 mb-2">Subject:</h4>
                <p className="text-gray-600">
                  {previewTemplate.subject.replace(/\{\{(\w+)\}\}/g, (match, variable) => {
                    const previewValues: { [key: string]: string } = {
                      name: 'John Doe',
                      company: 'Example Corp',
                      event_name: 'Annual Conference 2024',
                      event_date: 'March 15, 2024 at 9:00 AM',
                      event_venue: 'Convention Center',
                      google_maps_link: 'https://maps.google.com',
                      organizer_name: 'Jane Smith',
                      organizer_company: 'Event Organizers Inc.'
                    };
                    return previewValues[variable] || match;
                  })}
                </p>
              </div>
              <div className="prose prose-sm max-w-none">
                <h4 className="font-semibold text-gray-700 mb-2">Message:</h4>
                <div
                  className="bg-gray-50 p-4 rounded-lg"
                  dangerouslySetInnerHTML={{
                    __html: previewTemplate.body.replace(/\{\{(\w+)\}\}/g, (match, variable) => {
                      const previewValues: { [key: string]: string } = {
                        name: 'John Doe',
                        company: 'Example Corp',
                        event_name: 'Annual Conference 2024',
                        event_date: 'March 15, 2024 at 9:00 AM',
                        event_venue: 'Convention Center',
                        google_maps_link: 'https://maps.google.com',
                        organizer_name: 'Jane Smith',
                        organizer_company: 'Event Organizers Inc.'
                      };
                      return `<span class="bg-yellow-100 px-1 rounded" title="Variable: ${variable}">${previewValues[variable] || match}</span>`;
                    })
                  }}
                />
              </div>
            </div>
            <div className="p-6 border-t flex justify-end gap-2">
              <button
                onClick={() => setPreviewTemplate(null)}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Close
              </button>
              <button
                onClick={() => {
                  handleEdit(previewTemplate);
                  setPreviewTemplate(null);
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Edit Template
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailTemplateManager;